import { DatePipe } from '@angular/common';
import {
    Component,
    computed,
    DestroyRef,
    HostListener,
    inject,
    model,
    OnInit,
    Signal,
    ChangeDetectionStrategy
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import {
    DialogService,
    ShButtonComponent,
    ShIconComponent,
    ShInputTextComponent,
    ShListComponent,
    ShListItemComponent,
    ShPopoverComponent,
    ShSpinnerComponent,
    ShTableCellComponent,
    ShTableComponent,
    ShTableHeadComponent,
    ShTableRowComponent,
    TextValueAccessor
} from '@gbr-monorepo/gbr-shui';
import { SeasonStatus, SeasonWithUpdateBy } from '@gbr-monorepo/interfaces';
import { TranslateModule } from '@ngx-translate/core';
import { select, Store } from '@ngxs/store';
import { distinctUntilChanged, filter, startWith, tap } from 'rxjs';
import { UserState } from '../../../../auth/state/user.state';
import { BackHomeComponent } from '../../../../shared/components/back-home/back-home.component';
import { BasicDialogComponent } from '../../../../shared/components/basic-dialog/basic-dialog.component';
import { scrollOverAmount } from '../../../../shared/helpers/tiny-helpers';
import { ActivateSeason, DeleteSeason, LazyLoadSeasons, LoadSeasons } from '../../../state/season/season.action';
import { SeasonState } from '../../../state/season/season.state';

@Component({
    selector: 'app-season-overview',
    imports: [
        DatePipe,
        ReactiveFormsModule,
        RouterModule,
        TranslateModule,
        TextValueAccessor,
        BackHomeComponent,
        ShInputTextComponent,
        ShButtonComponent,
        ShTableComponent,
        ShTableRowComponent,
        ShTableHeadComponent,
        ShTableCellComponent,
        ShIconComponent,
        ShPopoverComponent,
        ShSpinnerComponent,
        DatePipe,
        ShListItemComponent,
        ShListComponent
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './seasons-overview.component.html'
})
export class SeasonsOverviewComponent implements OnInit {
    private readonly store = inject(Store);
    private readonly dialog = inject(DialogService);
    private readonly router = inject(Router);
    private readonly destroyRef = inject(DestroyRef);
    public seasonStatus = SeasonStatus;
    public readonly columnNames = computed(() => {
        const columnNames = [
            { label: 'seasons.season-overview.table.title', columns: 4 },
            { label: 'seasons.season-overview.table.creation-date', columns: 4 },
            { label: 'seasons.season-overview.table.date-range', columns: 4 },
            { label: 'seasons.season-overview.table.state', columns: 3 },
            { label: 'seasons.season-overview.table.updated-by', columns: 4 },
            { label: 'seasons.season-overview.table.updated-at', columns: 4 }
        ];
        return !this.isAdmin() ? columnNames : [...columnNames, { label: '', columns: 2 }];
    });
    protected readonly seasonStateTextColor = {
        [SeasonStatus.ACTIVE]: 'rgb(var(--ui-1))',
        [SeasonStatus.DRAFT]: 'rgb(var(--ui-2))',
        [SeasonStatus.CLOSED]: 'rgb(var(--ui-1))'
    };
    protected readonly draftSeasonStatus = SeasonStatus.DRAFT;
    protected readonly activeSeasonStatus = SeasonStatus.ACTIVE;
    protected readonly closedSeasonStatus = SeasonStatus.CLOSED;
    protected readonly seasons: Signal<SeasonWithUpdateBy[]> = select(SeasonState.getSeasons);
    protected readonly isLoading: Signal<boolean> = select(SeasonState.isLoadingSeasons);
    protected readonly isInitalLoading: Signal<boolean> = select(SeasonState.isSeasonsInitalLoading);
    protected readonly allSeasonsLoaded: Signal<boolean> = select(SeasonState.allSeasonsLoaded);
    protected readonly isAdmin: Signal<boolean> = select(UserState.isAdmin);
    protected readonly isProcessManager: Signal<boolean> = select(UserState.isProcessManager);
    protected readonly filteredSeasons: Signal<SeasonWithUpdateBy[]> = computed(() =>
        this.seasons().filter(season =>
            [season.id, season.title, season.updatedBy, season.startDate, season.endDate, season.status]
                .map(s => `${s}`.toLocaleLowerCase())
                .join()
                .includes(this.searchValue().toLocaleLowerCase())
        )
    );

    protected readonly searchControl = new FormControl('', { nonNullable: true });
    protected searchValue = model('');

    ngOnInit(): void {
        this.store.dispatch(new LoadSeasons());
        this.subscribeToSearchValueChanges();
    }

    @HostListener('window:scroll', ['$event'])
    onScroll(event: Event): void {
        if (!scrollOverAmount(event) || this.isLoading() || this.allSeasonsLoaded()) {
            return;
        }
        this.store.dispatch(new LazyLoadSeasons());
    }

    public handleClickOnSeason(seasonId: string): void {
        this.router.navigate(['/season-details', seasonId]);
    }

    public openDeleteDialog(seasonId: string): void {
        this.dialog
            .open(BasicDialogComponent, {
                width: '800px',
                title: 'seasons.season-overview.dialogs.delete-dialog.title',
                data: {
                    message: 'seasons.season-overview.dialogs.delete-dialog.text',
                    reject: 'seasons.season-overview.dialogs.delete-dialog.reject',
                    confirm: 'seasons.season-overview.dialogs.delete-dialog.accept'
                }
            })
            .afterClosed.pipe(
                filter(confirmed => !!confirmed),
                tap(() => this.store.dispatch(new DeleteSeason(seasonId))),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    public openSetActiveDialog(seasonId: string): void {
        this.dialog
            .open(BasicDialogComponent, {
                width: '800px',
                title: 'seasons.season-overview.dialogs.set-active-dialog.title',
                data: {
                    message: 'seasons.season-overview.dialogs.set-active-dialog.text',
                    reject: 'seasons.season-overview.dialogs.set-active-dialog.reject',
                    confirm: 'seasons.season-overview.dialogs.set-active-dialog.accept'
                }
            })
            .afterClosed.pipe(
                filter(confirmed => !!confirmed),
                tap(() => {
                    this.store.dispatch(new ActivateSeason(seasonId));
                    this.store.dispatch(new LoadSeasons());
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    public editSeason(seasonId: string): void {
        this.router.navigate(['/season/' + seasonId]);
    }

    private subscribeToSearchValueChanges(): void {
        this.searchControl.valueChanges
            .pipe(startWith(this.searchControl.value), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
            .subscribe(value => this.searchValue.set(value));
    }
}
